import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { PlanTable } from "../components/legal/PlanTable";
import { useReportCenter } from "../contexts/ReportCenterContext";
import { useAccountState } from "../hooks/useAccountState";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { api, type BillingSubscriptionData } from "../lib/api";
import { canUpgradeToPaid, isPaidPlan } from "../lib/billing";
import { logError, toUserMessage } from "../lib/errors";
import { resolvePlansReturnPath, type PlansLocationState } from "../lib/plansNavigation";
import { projectProductCatalogForPlans } from "../lib/productCatalog";
import { buildPublicLpDestinationHref, resolvePublicLpReturnTo } from "../lib/publicLpReturn";

type CheckoutPlanCode = "standard_monthly" | "executive_monthly";

export default function PlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled: globalHeaderActive } = useReportCenter();
  const { accountState, loading, error } = useAccountState();
  const { catalog, loading: catalogLoading, error: catalogError, retry: retryCatalog } = useProductCatalog();
  const catalogProjection = React.useMemo(
    () => catalog ? projectProductCatalogForPlans(catalog) : null,
    [catalog]
  );
  const inviteTrialPlan = catalog?.plans.find((plan) => plan.key === "invite_trial") ?? null;
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = React.useState<CheckoutPlanCode | null>(null);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [billingSubscription, setBillingSubscription] = React.useState<BillingSubscriptionData | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const billingAvailable = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false);
  const canStartNewCheckout = catalog !== null && billingAvailable && canUpgradeToPaid(accountState);
  const canOpenPortal = billingAvailable && isPaidPlan(accountState);
  const actionBusy = checkoutLoadingPlan !== null || portalLoading;
  const isAuthenticated = accountState?.authStatus === "authenticated";
  const publicLpReturnTo = React.useMemo(
    () => resolvePublicLpReturnTo(new URLSearchParams(location.search).get("returnTo")),
    [location.search]
  );
  const returnPath = React.useMemo(
    () => resolvePlansReturnPath(
      (location.state as PlansLocationState | null) ?? null,
      isAuthenticated,
      location.search
    ),
    [isAuthenticated, location.search, location.state]
  );
  const currentPlanKey = accountState?.plan === "paid"
    ? "standard"
    : accountState?.plan === "executive"
      ? "executive"
      : accountState?.plan === "free"
        ? "free"
        : accountState?.plan === "invite_trial"
          ? "invite_trial"
          : null;
  const currentPlanLabel = currentPlanKey
    ? catalog?.plans.find((plan) => plan.key === currentPlanKey)?.displayName ?? null
    : accountState?.plan === "internal" ? "社内プラン" : null;
  const isSubscriptionCanceled = canOpenPortal && billingSubscription?.cancelAtPeriodEnd === true;
  const canOpenPlanManagement = canOpenPortal;

  React.useEffect(() => {
    if (!canOpenPortal) {
      setBillingSubscription(null);
      return;
    }

    let disposed = false;

    api.getBillingSubscription()
      .then((subscription) => {
        if (!disposed) setBillingSubscription(subscription);
      })
      .catch((errorValue) => {
        if (disposed) return;
        logError(errorValue);
        setBillingSubscription(null);
      });

    return () => {
      disposed = true;
    };
  }, [canOpenPortal]);

  const openBillingPortal = React.useCallback(async () => {
    if (!canOpenPortal || portalLoading) return;
    setPortalLoading(true);
    setActionError(null);
    try {
      const session = await api.createBillingPortalSession();
      window.location.href = session.url;
    } catch (errorValue) {
      logError(errorValue);
      setActionError(toUserMessage(errorValue));
    } finally {
      setPortalLoading(false);
    }
  }, [canOpenPortal, portalLoading]);

  const startCheckout = React.useCallback(async (planCode: CheckoutPlanCode) => {
    const planKey = planCode === "standard_monthly" ? "standard" : "executive";
    const selectedPlan = catalog?.plans.find((plan) => plan.key === planKey);
    if (!canStartNewCheckout || checkoutLoadingPlan !== null || selectedPlan?.price.purchasable !== true) return;
    setCheckoutLoadingPlan(planCode);
    setActionError(null);
    try {
      const session = await api.createBillingCheckoutSession(planCode);
      window.location.href = session.url;
    } catch (errorValue) {
      logError(errorValue);
      setActionError(toUserMessage(errorValue));
    } finally {
      setCheckoutLoadingPlan(null);
    }
  }, [canStartNewCheckout, catalog, checkoutLoadingPlan]);

  const openPlanManagement = React.useCallback(() => {
    if (!canOpenPortal) return;
    void openBillingPortal();
  }, [canOpenPortal, openBillingPortal]);

  const planManagementActionLabel = actionBusy ? "移動中..." : "契約確認・プラン変更へ";
  const planManagementActionDisabled = loading || actionBusy || !canOpenPlanManagement;

  return (
    <main className="min-h-screen bg-[#FDF8F5] px-6 py-10 text-[#4A3E39] antialiased">
      <article className="mx-auto max-w-5xl rounded-[2rem] border border-[#EADBC8] bg-white px-6 py-8 shadow-[0_14px_28px_rgba(166,124,82,0.10)] md:px-10">
        {!globalHeaderActive ? (
          <header className="mb-8 flex items-center justify-between gap-4">
            <button type="button" onClick={() => navigate(returnPath)} className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
              ←戻る
            </button>
            <Link to={isAuthenticated ? "/home" : "/"} className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
              WizMate
            </Link>
          </header>
        ) : null}

        <div className="space-y-8 text-sm leading-7 text-[#5D4D46]">
          <section className="space-y-3">
            <h1 className="text-2xl font-bold text-[#5D4D46]">料金プラン</h1>
            <p>WizMateの料金プランと主な利用上限を確認できます。</p>
            {canStartNewCheckout ? <p>利用したいプランを選んで、お申し込みへ進んでください。</p> : null}
            {loading ? <p className="text-xs font-bold text-[#9C867D]">アカウント状態を確認中です。</p> : null}
            {error ? <p className="text-xs font-bold text-[#A46D4C]">ログイン状態を確認できませんでした。ログイン後にプランを選択してください。</p> : null}
            {!loading && accountState?.authStatus === "authenticated" && !billingAvailable ? (
              <p className="text-xs font-bold text-[#A46D4C]">現在、このアカウントではプラン変更機能を利用できません。</p>
            ) : null}
            {actionError ? <p role="alert" className="text-xs font-bold text-[#A46D4C]">{actionError}</p> : null}
            {isAuthenticated && currentPlanLabel ? (
              <div className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F1] px-4 py-4 text-xs leading-6 text-[#6B5851]">
                <p className="font-black text-[#5D4D46]">現在のプラン：{currentPlanLabel}</p>
                {canOpenPortal && isSubscriptionCanceled ? (
                  <>
                    <p className="mt-2 font-bold text-[#5D4D46]">{currentPlanLabel}は解約済みです。</p>
                    <p>契約内容の確認、プラン変更、解約は次の決済管理画面で行えます。</p>
                    <p>プランを変更する場合は「サブスクリプションを更新」、</p>
                    <p>解約する場合は「サブスクリプションをキャンセル」を選択してください。</p>
                    <p>有効期限までは引き続き利用できます。</p>
                    <p>次回以降の請求は行われません。</p>
                    <p>日割り返金はありません。</p>
                  </>
                ) : canOpenPortal ? (
                  <>
                    <p className="mt-2">
                      契約内容の確認、プラン変更、解約は次の決済管理画面で行えます。
                    </p>
                    <p>プランを変更する場合は「サブスクリプションを更新」、</p>
                    <p>解約する場合は「サブスクリプションをキャンセル」を選択してください。</p>
                    <p>解約した場合も、すでにお支払い済みの期間中は引き続き利用できます。</p>
                    <p>日割り返金はありません。</p>
                  </>
                ) : null}
                {canOpenPlanManagement ? (
                  <button
                    type="button"
                    onClick={openPlanManagement}
                    disabled={planManagementActionDisabled}
                    className="mt-4 rounded-2xl bg-[#7C5A4E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#6D4D42] disabled:cursor-not-allowed disabled:bg-[#D8C7BA]"
                  >
                    {planManagementActionLabel}
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>

          {catalogLoading ? (
            <section aria-live="polite" className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-5 py-5 font-bold text-[#7B6861]">
              料金・利用条件を読み込んでいます。
            </section>
          ) : catalogError || !catalogProjection ? (
            <section role="alert" className="rounded-[1.4rem] border border-[#F0C8BA] bg-[#FFF7F1] px-5 py-5">
              <p className="font-bold text-[#A46D4C]">料金・利用条件を取得できませんでした。もう一度お試しください。</p>
              <button
                type="button"
                onClick={retryCatalog}
                className="mt-4 rounded-2xl bg-[#7C5A4E] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#6D4D42]"
              >
                再試行
              </button>
            </section>
          ) : (
            <>
          <section className="grid gap-4 md:grid-cols-3">
            {catalogProjection.cards.map((plan) => {
              const isCurrentPlan = currentPlanKey === plan.key;
              const checkoutPlanCode: CheckoutPlanCode | null =
                plan.key === "standard" ? "standard_monthly"
                : plan.key === "executive" ? "executive_monthly"
                : null;
              const checkoutButtonLabel = `${plan.name}を申し込む`;

              return (
                <article key={plan.key} className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-5 py-5">
                  {isCurrentPlan ? (
                    <span className="mb-3 inline-flex rounded-full bg-[#F4E6DA] px-3 py-1 text-[11px] font-black text-[#7C5A4E]">
                      現在利用中
                    </span>
                  ) : null}
                  <p className="text-base font-black text-[#5D4D46]">{plan.name}</p>
                  <p className="mt-2 text-lg font-black text-[#7C5A4E]">{plan.priceLabel}</p>
                  <p className="mt-2 text-xs leading-6 text-[#7B6861]">{plan.description}</p>

                  {plan.key === "free" ? (
                    <p className="mt-5 rounded-2xl bg-[#FDF8F5] px-4 py-3 text-xs font-bold text-[#7B6861]">利用登録後、無料で開始できます。</p>
                  ) : null}
                  {canStartNewCheckout && plan.purchasable && checkoutPlanCode ? (
                    <button
                      type="button"
                      onClick={() => {
                        void startCheckout(checkoutPlanCode);
                      }}
                      disabled={loading || actionBusy}
                      className="mt-5 w-full rounded-2xl bg-[#7C5A4E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#6D4D42] disabled:cursor-not-allowed disabled:bg-[#D8C7BA]"
                    >
                      {checkoutLoadingPlan === checkoutPlanCode ? "移動中..." : checkoutButtonLabel}
                    </button>
                  ) : null}
                  {!isAuthenticated && plan.purchasable && checkoutPlanCode ? (
                    <Link
                      to="/register"
                      className="mt-5 inline-flex w-full justify-center rounded-2xl bg-[#7C5A4E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#6D4D42]"
                    >
                      無料ではじめる
                    </Link>
                  ) : null}
                </article>
              );
            })}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">主要な利用上限</h2>
            <PlanTable
              ariaLabel="プラン別の主な利用上限"
              columns={catalogProjection.columns}
              rows={catalogProjection.limitRows}
            />
            {catalogProjection.unlimitedNotice ? <p>※ {catalogProjection.unlimitedNotice}</p> : null}
          </section>

          {catalogProjection.frequencyRows.length > 0 ? <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">定期配信頻度</h2>
            <PlanTable
              ariaLabel="プラン別の定期配信頻度"
              columns={catalogProjection.columns}
              rows={catalogProjection.frequencyRows}
            />
          </section> : null}
            </>
          )}

          <section className="space-y-3">
            <h2 className="text-base font-bold text-[#5D4D46]">確認事項</h2>
            {inviteTrialPlan ? (
              <p>
                {inviteTrialPlan.displayName}：{inviteTrialPlan.description}。お持ちの場合は、メニューの「特典コードを入力」から適用してください。
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3 text-xs font-bold">
              <Link to={buildPublicLpDestinationHref("/terms", publicLpReturnTo)} className="underline underline-offset-4">
                利用規約
              </Link>
              <Link to={buildPublicLpDestinationHref("/privacy", publicLpReturnTo)} className="underline underline-offset-4">
                プライバシーポリシー
              </Link>
              <Link to={buildPublicLpDestinationHref("/commercial-transactions", publicLpReturnTo)} className="underline underline-offset-4">
                特定商取引法に基づく表記
              </Link>
            </div>
          </section>
        </div>
      </article>
    </main>
  );
}
